import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import { getScheduleLesson, saveScheduleLesson } from '@common/api/scheduleLessons';
import scheduleLessonTypes from '@common/constants/ScheduleLessonTypes';
import { IServiceModel } from '@common/models/Services/Service';

import { IScheduleLessonsModel } from '../Schedules/ScheduleLessons';
import ClientHomework from './ClientHomework';
import ClientLesson from './ClientLesson';
import ClientScheduleLessonTeacher from './ClientScheduleLessonTeacher';
import ClientScheduleProgram from './ClientScheduleProgram';

const ClientScheduleLesson = types
	.model('ClientScheduleLesson', {
		id: types.identifierNumber,
		name: types.maybeNull(types.optional(types.string, '')),
		schedule_program: types.maybeNull(ClientScheduleProgram),
		lesson: types.maybeNull(ClientLesson),
		start: types.maybeNull(types.string),
		type: types.maybeNull(types.string),
		duration: types.maybeNull(types.number),
		deadline: types.maybeNull(types.string),
		order_id: types.maybeNull(types.number),
		schedule_id: types.number,
		teacher: types.maybeNull(ClientScheduleLessonTeacher),
		toHomework: types.maybeNull(ClientHomework),
	})
	.volatile(() => ({
		loading: false,
		number: 0,
	}))
	.views((self) => ({
		get dateTime() {
			return DateTime.fromSQL(self.start);
		},
		get dateTimeStart() {
			return DateTime.fromSQL(self.start);
		},
		get dateTimeEnd() {
			return DateTime.fromSQL(self.start).plus({ minutes: self.duration });
		},
	}))
	.views((self) => ({
		get dateString() {
			return self.dateTime.toISODate();
		},
		get timeString() {
			return self.dateTimeStart.toLocaleString(DateTime.TIME_24_SIMPLE);
		},
		get timesString() {
			return `${self.dateTimeStart.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${self.dateTimeEnd.toLocaleString(DateTime.TIME_24_SIMPLE)}`;
		},
		getZoomLink(service: IServiceModel) {
			const zoomLink: Record<string, string | null> = {
				Academy: self.schedule_program?.zoom_link,
				Lyceum: self.teacher?.zoom_on_off && self.teacher?.zoom_link,
				TIS:
					(self.type === scheduleLessonTypes.Life || self.type === scheduleLessonTypes.Webinar) &&
					self.schedule_program?.zoom_link,
			};

			return zoomLink[service?.name] || null;
		},
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getScheduleLesson(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove() {
			const parent = getParent<IScheduleLessonsModel>(self, 2);
			parent.removeItem(self as IClientScheduleLessonModel);
		},
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield saveScheduleLesson(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
	}));

export default ClientScheduleLesson;

export interface IClientScheduleLessonModel extends Instance<typeof ClientScheduleLesson> {}
